import './styles.css';
import * as ReactDOM from 'react-dom/client';
import './i18n';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import React from 'react';
import * as Sentry from '@sentry/react';
import SplashScreen from './shared/components/SplashScreen';
import { GcalClient, GcalProvider } from '@geerd-nx/gcal';
import { GCAL } from './env';
import ErrorPage from './pages/Error/ErrorPage';
import { BetterAuthGcalProvider } from './auth/BetterAuthGcalProvider';
import { USE_BETTER_AUTH } from './auth/betterAuthClient';

Sentry.init({
  // Sentry-compatible DSN. Set NX_SENTRY_DSN to point at Bugsink (self-hosted)
  // in production; falls back to the existing Sentry SaaS DSN if unset.
  dsn:
    import.meta.env['NX_SENTRY_DSN'] ||
    'https://d3b33f68bb25e904b06103be56550f7c@o4506373556600832.ingest.us.sentry.io/4507406893711360',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled

  tracePropagationTargets: [
    /^https:\/\/(\w+\.)?abs-elevate\.ma/,
    /^https:\/\/staging\.ocw-client\.pages\.dev/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
});

const AuthApp = React.lazy(() => import('./auth/App'));
const App = React.lazy(() => import('./app'));
const TermsPage = React.lazy(() => import('./auth/screens/Legal/TermsPage'));
const PrivacyPage = React.lazy(
  () => import('./auth/screens/Legal/PrivacyPage')
);

// Wrap Routes so Sentry reports parameterized route names (e.g. /course/:id)
// for navigation transactions instead of raw URLs with interpolated IDs.
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const gcalClient = new GcalClient({
  ...GCAL,
  LoadingComponent: <SplashScreen />,
});
declare global {
  interface Window {
    Countly: any;
  }
}

// Prevent mouse scroll from changing number inputs values
document.addEventListener(
  'wheel',
  function (event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'number'
    ) {
      event.preventDefault();
    }
  },
  { passive: false }
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Sentry.ErrorBoundary fallback={<ErrorPage />}>
    <Router>
      <SentryRoutes>
        <Route
          path="/auth/*"
          element={
            <React.Suspense fallback={<SplashScreen />}>
              <AuthApp />
            </React.Suspense>
          }
        />
        <Route
          path="/terms-of-use"
          element={
            <React.Suspense fallback={<SplashScreen />}>
              <TermsPage />
            </React.Suspense>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <React.Suspense fallback={<SplashScreen />}>
              <PrivacyPage />
            </React.Suspense>
          }
        />
        <Route
          path="/*"
          element={
            <React.Suspense fallback={<SplashScreen />}>
              {USE_BETTER_AUTH ? (
                <BetterAuthGcalProvider>
                  <App />
                </BetterAuthGcalProvider>
              ) : (
                <GcalProvider client={gcalClient}>
                  <App />
                </GcalProvider>
              )}
            </React.Suspense>
          }
        />
      </SentryRoutes>
    </Router>
  </Sentry.ErrorBoundary>
);
